import React, { useEffect, useState } from "react";
import { ArrowUpRight, Mail, MailOpen } from "lucide-react";
import type { StudentMessage } from "../hooks/useStudentMessages";

type Props = {
    messages: StudentMessage[];
    loading: boolean;
    error: string;
    onSetRead: (messageId: string, read: boolean) => Promise<void>;
};

function toDate(value: StudentMessage["createdAt"]) {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "string") {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") return value.toDate();
    return null;
}

function formatDate(value: StudentMessage["createdAt"], timezone?: string) {
    const date = toDate(value);
    if (!date) return "";
    try {
        return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: timezone || "Asia/Kolkata" }).format(date);
    } catch {
        return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(date);
    }
}

function formatPayment(message: StudentMessage) {
    const amount = message.details?.amountMinor;
    if (typeof amount !== "number") return "";
    try {
        return new Intl.NumberFormat("en-IN", { style: "currency", currency: message.details?.currency || "INR" }).format(amount / 100);
    } catch {
        return `${message.details?.currency || "INR"} ${(amount / 100).toFixed(2)}`;
    }
}

export default function StudentMessages({ messages, loading, error, onSetRead }: Props) {
    const [selectedId, setSelectedId] = useState("");
    const selected = messages.find((message) => message.id === selectedId) || null;

    useEffect(() => {
        if (selectedId && !messages.some((message) => message.id === selectedId)) setSelectedId("");
    }, [messages, selectedId]);

    const openMessage = (message: StudentMessage) => {
        setSelectedId(message.id);
        if (!message.readAt) void onSetRead(message.id, true);
    };

    return <section aria-labelledby="student-messages-title" className="space-y-5">
        <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Student inbox</p>
            <h1 id="student-messages-title" className="mt-1 text-2xl font-black text-slate-950">Messages</h1>
            <p className="mt-1 text-sm text-slate-600">Confirmations and updates from MockZo appear here.</p>
        </div>

        {error && <p role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">{error}</p>}
        {loading && <p role="status" className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">Loading messages…</p>}
        {!loading && !error && messages.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center"><Mail className="mx-auto size-9 text-slate-400" /><h2 className="mt-3 font-bold text-slate-900">No messages yet</h2><p className="mt-1 text-sm text-slate-600">Enrollment, payment, and booking confirmations will appear here.</p></div>}

        {!loading && messages.length > 0 && <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.35fr)]">
            <div aria-label="Message list" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                {messages.map((message) => <button key={message.id} type="button" onClick={() => openMessage(message)} className={`flex w-full gap-3 border-b border-slate-100 px-4 py-4 text-left transition last:border-b-0 hover:bg-blue-50/60 ${selectedId === message.id ? "bg-blue-50" : "bg-white"}`} aria-label={`${message.readAt ? "Read" : "Unread"}: ${message.title}`}>
                    <span className={`mt-1 grid size-8 shrink-0 place-items-center rounded-full ${message.readAt ? "bg-slate-100 text-slate-500" : "bg-blue-100 text-blue-700"}`}>{message.readAt ? <MailOpen className="size-4" /> : <Mail className="size-4" />}</span>
                    <span className="min-w-0 flex-1"><span className={`block truncate text-sm ${message.readAt ? "font-semibold text-slate-700" : "font-black text-slate-950"}`}>{message.title}</span><span className="mt-1 block truncate text-xs text-slate-500">{message.body}</span><span className="mt-2 block text-[11px] font-semibold text-slate-400">{formatDate(message.createdAt)}</span></span>
                    {!message.readAt && <span className="mt-2 size-2 shrink-0 rounded-full bg-blue-600" aria-hidden="true" />}
                </button>)}
            </div>

            <article className="min-h-64 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                {!selected && <div className="grid min-h-52 place-items-center text-center text-sm text-slate-500">Select a message to read it.</div>}
                {selected && <div className="space-y-5">
                    <div><div className="flex flex-wrap items-start justify-between gap-3"><h2 className="text-xl font-black text-slate-950">{selected.title}</h2><button type="button" className="text-xs font-bold text-blue-700 hover:underline" onClick={() => void onSetRead(selected.id, !Boolean(selected.readAt))}>{selected.readAt ? "Mark unread" : "Mark read"}</button></div><p className="mt-1 text-xs font-semibold text-slate-400">{formatDate(selected.createdAt)}</p></div>
                    <p className="text-sm leading-6 text-slate-700">{selected.body}</p>
                    {selected.details && <dl className="grid gap-3 rounded-xl bg-slate-50 p-4 text-sm sm:grid-cols-2">
                        {(selected.details.courseTitle || selected.details.itemTitle) && <Detail label={selected.details.kind === "INTERVIEW" ? "Interview" : "Program"} value={selected.details.courseTitle || selected.details.itemTitle || ""} />}
                        {selected.details.runTitle && <Detail label="Cohort" value={selected.details.runTitle} />}
                        {selected.details.targetRole && <Detail label="Target role" value={selected.details.targetRole} />}
                        {selected.details.startAt && <Detail label="Starts" value={formatDate(selected.details.startAt, selected.details.timezone)} />}
                        {selected.details.endAt && <Detail label="Ends" value={formatDate(selected.details.endAt, selected.details.timezone)} />}
                        {formatPayment(selected) && <Detail label="Payment" value={`${formatPayment(selected)} · Confirmed`} />}
                        {selected.details.paymentReference && <Detail label="Payment reference" value={selected.details.paymentReference} />}
                    </dl>}
                    {selected.action?.href?.startsWith("/") && <a href={selected.action.href} className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800">{selected.action.label || "Open"}<ArrowUpRight className="size-4" /></a>}
                </div>}
            </article>
        </div>}
    </section>;
}

function Detail({ label, value }: { label: string; value: string }) {
    return <div><dt className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</dt><dd className="mt-1 break-words font-semibold text-slate-800">{value}</dd></div>;
}
